import { MetricsDictionary } from '@tw/types/module/metrics/metrics';
import { divide, sum } from './utils';

export const pixelMetrics: MetricsDictionary = {
  pixelRoas: {
    key: 'pixelRoas',
    label: 'Pixel ROAS',
    shortLabel: 'ROAS',
    showInCreativeCard: ['product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku', 'Segments'],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    format: 'decimal',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: true,
    showInInfluencersHub: true,
    showInPixelByDefault: true,
    showInReport: true,
    allowOrderBy: true,
    pixelIndex: 4,
    calculateSum: (items) => {
      return divide(items, 'pixelConversionValue', 'spend');
    },
  },
  pixelNcRoas: {
    key: 'pixelNcRoas',
    label: 'Pixel New Customers ROAS',
    shortLabel: 'NC ROAS',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    format: 'decimal',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInInfluencersHub: true,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelNcConversionValue', 'spend');
    },
  },
  pixelPurchases: {
    key: 'pixelPurchases',
    label: 'Pixel Purchases',
    shortLabel: 'Purchases',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    showInInfluencersHub: true,
    format: 'decimal',
    toFixed: 1,
    minimumFractionDigits: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: true,
    showInReport: true,
    allowOrderBy: true,
    pixelIndex: 5,
    calculateSum: (items) => {
      return sum(items, 'pixelPurchases');
    },
  },
  pixelNcPurchases: {
    key: 'pixelNcPurchases',
    label: 'Pixel New Customer Purchases',
    shortLabel: 'NCP',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    showInInfluencersHub: true,
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelNcPurchases');
    },
  },
  pixelConversionValue: {
    key: 'pixelConversionValue',
    label: 'Pixel CV',
    shortLabel: 'CV',
    showInCreativeCard: ['product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: true,
    showInPixelByDefault: true,
    showInReport: true,
    allowOrderBy: true,
    pixelIndex: 6,
    calculateSum: (items) => {
      return sum(items, 'pixelConversionValue');
    },
  },
  pixelNcConversionValue: {
    key: 'pixelNcConversionValue',
    label: 'Pixel New Customer CV',
    shortLabel: 'NCV',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelNcConversionValue');
    },
  },
  pixelVisitors: {
    key: 'pixelVisitors',
    label: 'Pixel Sessions',
    shortLabel: 'Sessions',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: true,
    showInInfluencersHub: true,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelVisitors');
    },
  },
  pixelUniqueVisitors: {
    key: 'pixelUniqueVisitors',
    label: 'Pixel Unique Visitors',
    shortLabel: 'Unique Visitors',
    showInCreativeCard: [],
    showInCreativeTable: [],
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInInfluencersHub: true,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelUniqueVisitors');
    },
  },
  pixelCostPerVisitor: {
    key: 'pixelCostPerVisitor',
    label: 'Pixel Cost Per Visitors',
    shortLabel: 'CPV',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelUniqueVisitors');
    },
  },
  pixelNewVisitors: {
    key: 'pixelNewVisitors',
    label: 'Pixel New Visitors',
    shortLabel: 'NV',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelNewVisitors');
    },
  },
  pixelCostPerNewVisitor: {
    key: 'pixelCostPerNewVisitor',
    label: 'Pixel Cost Per New Visitors',
    shortLabel: 'Cost NV',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelNewVisitors');
    },
  },
  pixelUniqueAtc: {
    key: 'pixelUniqueAtc',
    label: 'Pixel Unique Add To Carts',
    shortLabel: 'ATC',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    showInInfluencersHub: true,
    allowOrderBy: false,
    calculateSum: (items) => {
      return sum(items, 'pixelUniqueAtc');
    },
  },
  pixelCostPerAtc: {
    key: 'pixelCostPerAtc',
    label: 'Pixel Cost Per Add to Cart',
    shortLabel: 'Cost ATC',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelUniqueAtc');
    },
  },
  pixelCpa: {
    key: 'pixelCpa',
    label: 'Pixel CPA',
    shortLabel: 'CPA',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments', 'product', 'sku'],
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: true,
    showInPixelByDefault: true,
    showInInfluencersHub: true,
    showInReport: true,
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    allowOrderBy: true,
    pixelIndex: 7,
    valueIsNegative: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelPurchases');
    },
  },
  pixelNcCpa: {
    key: 'pixelNcCpa',
    label: 'Pixel New Customer CPA',
    shortLabel: 'NC CPA',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName', 'product', 'sku'],
    showInCreativeTable: [
      'video',
      'image',
      'copy',
      'ad',
      'adName',
      'product',
      'Segments',
      'product',
      'sku',
    ],
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: true,
    showInInfluencersHub: true,
    showInReport: true,
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    allowOrderBy: true,
    pixelIndex: 10,
    valueIsNegative: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelNcPurchases');
    },
  },
  pixelAov: {
    key: 'pixelAov',
    label: 'Pixel AOV',
    shortLabel: 'AOV',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments'],
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: true,
    showInInfluencersHub: true,
    showInRules: ['account', 'campaign', 'adset', 'ad'],
    showInReport: true,
    allowOrderBy: true,
    pixelIndex: 8,
    calculateSum: (items) => {
      return divide(items, 'pixelConversionValue', 'pixelPurchases');
    },
  },
  pixelNcAov: {
    key: 'pixelNcAov',
    label: 'Pixel New Customer AOV',
    shortLabel: 'NC AOV',
    showInCreativeCard: ['video', 'image', 'copy', 'ad', 'adName'],
    showInCreativeTable: ['video', 'image', 'copy', 'ad', 'adName', 'Segments'],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelNcConversionValue', 'pixelNcPurchases');
    },
  },
  pixelConversionRate: {
    key: 'pixelConversionRate',
    label: 'Pixel Conversion Rate',
    shortLabel: 'CR',
    showInCreativeCard: ['product'],
    showInCreativeTable: ['product'],
    showInInfluencersHub: true,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelPurchases', 'pixelVisitors');
    },
  },
  pixelNcConversionRate: {
    key: 'pixelNcConversionRate',
    label: 'Pixel New Customer Conversion Rate',
    shortLabel: 'NC CR',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelNcPurchases', 'pixelNewVisitors');
    },
  },
  pixelEmailSignup: {
    key: 'pixelEmailSignup',
    label: 'Pixel Email Sign Up',
    shortLabel: 'Email SU',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'decimal',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelEmailSignup');
    },
  },
  pixelCostPerEmailSignup: {
    key: 'pixelCostPerEmailSignup',
    label: 'Pixel Cost Per Email Sign Up',
    shortLabel: 'Cost SU',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'spend', 'pixelEmailSignup');
    },
  },
  pixelEmailSignupRate: {
    key: 'pixelEmailSignupRate',
    label: 'Pixel Email Sign Up Rate',
    shortLabel: 'Email SUR',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelEmailSignup', 'pixelUniqueVisitors');
    },
  },
  pixelCogs: {
    key: 'pixelCogs',
    label: 'Pixel Cost of Goods',
    shortLabel: 'COGS',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelCogs');
    },
  },
  pixelNcCogs: {
    key: 'pixelNcCogs',
    label: 'Pixel New Customer Cost of Goods',
    shortLabel: 'NC COGS',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return sum(items, 'pixelNcCogs');
    },
  },
  pixelProfit: {
    key: 'pixelProfit',
    label: 'Pixel Profit',
    shortLabel: 'Profit',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: true,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      if (items[0]?.pixelProfit) {
        return sum(items, 'pixelProfit');
      }
      return sum(items, 'pixelConversionValue') - sum(items, 'pixelCogs') - sum(items, 'spend');
    },
  },
  pixelCvDelta: {
    key: 'pixelCvDelta',
    label: 'Pixel Conversion Value Delta',
    shortLabel: 'CVD',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'currency',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      if (items[0]?.pixelCvDelta) {
        return sum(items, 'pixelCvDelta');
      }
      return sum(items, 'pixelConversionValue') - sum(items, 'conversionValue');
    },
  },
  pixelNewVisitorPerc: {
    key: 'pixelNewVisitorPerc',
    label: 'Pixel New Visitor Percent',
    shortLabel: 'NVP',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelNewVisitors', 'pixelUniqueVisitors');
    },
  },
  pixelNcPurchasesPerc: {
    key: 'pixelNcPurchasesPerc',
    label: 'Pixel New Customer Purchases Percent',
    shortLabel: 'NC PP',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelNcPurchases', 'pixelPurchases');
    },
  },
  pixelAvgPageViews: {
    key: 'pixelAvgPageViews',
    label: 'Pixel Average Page Views',
    shortLabel: 'Avg PV',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'decimal',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelPageViews', 'pixelVisitors');
    },
  },
  pixelAvgTimeOnSite: {
    key: 'pixelAvgTimeOnSite',
    label: 'Pixel Average Session Duration',
    shortLabel: 'Avg SD',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'duration',
    toFixed: 0,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelTimeOnSite', 'pixelNonBouncedVisitors');
    },
  },
  pixelBounceRate: {
    key: 'pixelBounceRate',
    label: 'Pixel Bounce Rate',
    shortLabel: 'Bounce Rate',
    showInCreativeCard: [],
    showInCreativeTable: [],
    showInInfluencersHub: false,
    format: 'percent',
    toFixed: 2,
    type: 'pixel',
    showInCreativeByDefault: false,
    showInPixelByDefault: false,
    allowOrderBy: true,
    calculateSum: (items) => {
      return divide(items, 'pixelBounces', 'pixelVisitors');
    },
  },
};
